import { DecksRepository } from '@lib/api/repository/decks/decks.repository';
import {
  DeckEntryInput,
  DeckImportFormat,
  DeckInput,
  DeckPublicationInput,
  DeckUpdateInput,
  OptimizedPurchaseEntryInput,
} from '@lib/types';
import {
  DeckCardPage,
  DeckCardSearch,
  DeckValuation,
} from '@lib/types/deck-editor';
import {
  DeckSaleInput,
  ManageDeckSaleInput,
  PublishDeckSaleInput,
} from '@lib/types/deck-sale';

export default class DecksService {
  constructor(private repository: DecksRepository) {}

  prepareSale(id: string, signal?: AbortSignal) {
    return this.repository.prepareSale(id, signal);
  }

  quoteSale(id: string, input: DeckSaleInput, signal?: AbortSignal) {
    return this.repository.quoteSale(id, input, signal);
  }

  publishSale(id: string, input: PublishDeckSaleInput) {
    return this.repository.publishSale(id, input);
  }

  manageSale(deckId: string, saleId: string, input: ManageDeckSaleInput) {
    return this.repository.manageSale(deckId, saleId, input);
  }

  publicSales(page: number, search?: string, signal?: AbortSignal) {
    return this.repository.publicSales(page, search, signal);
  }

  publicSale(id: string, signal?: AbortSignal) {
    return this.repository.publicSale(id, signal);
  }

  ownedSale(saleId: string, signal?: AbortSignal) {
    return this.repository.ownedSale(saleId, signal);
  }

  saleOrders(deckId: string, saleId: string, signal?: AbortSignal) {
    return this.repository.saleOrders(deckId, saleId, signal);
  }

  searchCards(
    id: string,
    filters: DeckCardSearch,
    signal?: AbortSignal,
  ): Promise<DeckCardPage> {
    return this.repository.searchCards(id, filters, signal);
  }

  value(
    id: string,
    entries: DeckEntryInput[],
    signal?: AbortSignal,
  ): Promise<DeckValuation> {
    return this.repository.value(id, entries, signal);
  }

  remove(id: string) {
    return this.repository.remove(id);
  }

  listMine(signal?: AbortSignal) {
    return this.repository.listMine(signal);
  }

  find(id: string, signal?: AbortSignal) {
    return this.repository.find(id, signal);
  }

  findPublic(id: string, signal?: AbortSignal) {
    return this.repository.findPublic(id, signal);
  }

  create(input: DeckInput) {
    return this.repository.create(input);
  }

  update(id: string, input: DeckUpdateInput) {
    return this.repository.update(id, input);
  }

  replaceEntries(
    id: string,
    entries: DeckEntryInput[],
    expectedUpdatedAt?: string,
  ) {
    return this.repository.replaceEntries(id, entries, expectedUpdatedAt);
  }

  previewImport(gameCode: string, content: string, format: DeckImportFormat) {
    return this.repository.previewImport(gameCode, content, format);
  }

  importRows(
    id: string,
    content: string,
    format: DeckImportFormat,
    resolutions: Array<{
      sourceKey: string;
      cardId: string;
      printingId?: string;
    }>,
    expectedUpdatedAt?: string,
  ) {
    return this.repository.importRows(
      id,
      content,
      format,
      resolutions,
      expectedUpdatedAt,
    );
  }

  resolveImportRow(
    id: string,
    rowId: string,
    cardId: string,
    expectedUpdatedAt?: string,
  ) {
    return this.repository.resolveImportRow(
      id,
      rowId,
      cardId,
      expectedUpdatedAt,
    );
  }

  budget(id: string, considerCart: boolean) {
    return this.repository.budget(id, considerCart);
  }

  budgetEntries(entries: OptimizedPurchaseEntryInput[], considerCart: boolean) {
    return this.repository.budgetEntries(entries, considerCart);
  }

  publish(id: string, input: DeckPublicationInput) {
    return this.repository.publish(id, input);
  }

  updateOffer(id: string, status: 'active' | 'paused') {
    return this.repository.updateOffer(id, status);
  }
}
